/**
 * 5-minute countdown timer screen.
 *
 * 2026-05-25 redesign: minimal warm-beige egg timer with a black LCD,
 * restrained cyan edge light, and a low physical shadow.
 *
 * On finish: auto-advance to TraceInput.
 */
import { useEffect } from 'react';
import type { PoolCard } from '../state/cardPool';
import { useTimer, formatRemaining } from '../timer/useTimer';
import { playTimerEndCue, playTimerEarlyEndCue } from '../timer/audio';
import { startTimerDoneTitleAlert } from '../timer/titleAlert';

interface TimerProps {
  card: PoolCard;
  durationMs?: number;
  onRoundEnded: () => void;
}

const FIVE_MIN = 5 * 60 * 1000;

export function Timer({ card, durationMs = FIVE_MIN, onRoundEnded }: TimerProps) {
  const { remaining, running, start, stop } = useTimer({
    durationMs,
    onFinish: () => {
      // Natural elapse — three-pass cue + advance.
      playTimerEndCue();
      startTimerDoneTitleAlert(card.title);
      onRoundEnded();
    },
  });

  function endEarly() {
    // Manual exit: a single soft chime, then advance. Deliberately bypass
    // complete() because that fires useTimer.onFinish, which would also play
    // the three-pass cue (= 1 + 3 = 4 chimes total). stop() halts the timer
    // without firing onFinish.
    playTimerEarlyEndCue();
    stop();
    onRoundEnded();
  }

  useEffect(() => {
    start();
    return () => {
      document.title = '每日启动器';
    };
  }, [start]);

  useEffect(() => {
    if (!running) return;
    const { total } = formatRemaining(remaining);
    document.title = `${total} · ${card.title} · 每日启动器`;
  }, [running, remaining, card.title]);

  const f = formatRemaining(remaining);
  const progress = Math.max(0, Math.min(1, 1 - remaining / durationMs));

  return (
    <section className="panel timer">
      <span className="tiny-label">
        <i className="dot cyan" /> 这一轮 5 分钟
      </span>
      <div className="timer-device-wrap">
        <div className="timer-device" role="img" aria-label={`剩余 ${f.total},正在做「${card.title}」`}>
          <div className="timer-screen">
            <div className="timer-time">
              <span>{f.m.padStart(2, '0')}</span>
              <span className="timer-colon">:</span>
              <span>{f.s}</span>
            </div>
            <div className="timer-units">
              <span>M</span>
              <span>S</span>
            </div>
          </div>
          <div className="timer-device-brand">5 MIN</div>
          <div className="timer-progress-slot" aria-hidden="true">
            <span style={{ width: `${progress * 100}%` }} />
          </div>
        </div>
      </div>
      <article className="timer-card-detail" aria-label="当前便签">
        <header>
          <span className="tiny-label">
            <i className="dot cyan" /> 当前便签
          </span>
          <strong>{card.title}</strong>
        </header>
        <p className="card-row action-row">
          <span className="row-label cyan">行动</span>
          <span className="row-text">{card.action}</span>
        </p>
        {card.actionHistory.length > 0 && (
          <details className="action-history">
            <summary className="muted tiny">
              已根据你的反馈调整过 {card.actionHistory.length} 次
            </summary>
            <ol>
              {card.actionHistory.map((action, i) => (
                <li key={i}>{action}</li>
              ))}
            </ol>
          </details>
        )}
      </article>
      <div className="actions actions-between">
        <span className="muted tiny">5 分钟不是考试,只是一个入口。能回来,比硬撑更重要。</span>
        <button className="btn-ghost" type="button" onClick={endEarly}>
          提前结束
        </button>
      </div>
    </section>
  );
}
